import {
    type CSSProperties,
    useState,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type DateTimePickerDateRange,
    type IDateSelectorRef,
    NCoreUIKitLocalize,
    formatDateTime,
    DateSelector,
    RadioButton,
    Button,
    Switch
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "DateSelector";

const USAGE_CODE = `const [date, setDate] = useState<Date>();

<DateSelector
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const RANGE_CODE = `const [dateRange, setDateRange] = useState<DateTimePickerDateRange>();

<DateSelector
    multipleSelectMinimumRequiredDayCount={3}
    selectMultipleObject={setDateRange}
    multipleSelectDayLimit={7}
    selectObject={() => undefined}
    dateRange={dateRange}
    variant="range"
/>`;

const LIMITS_CODE = `<DateSelector
    maxDate={addDays(new Date(), 10)}
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    minDate={new Date()}
    variant="single"
    date={date}
/>`;

const REF_CODE = `const calendarRef = useRef<IDateSelectorRef>(null);

<DateSelector
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    ref={calendarRef}
    variant="single"
    date={date}
/>

calendarRef.current?.changeCurrentMonth(new Date());`;

const WEEKS_CODE = `<DateSelector
    selectMultipleObject={() => undefined}
    isShowWeekNumber={true}
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const MONTHS_CODE = `<DateSelector
    selectMultipleObject={setDateRange}
    selectObject={() => undefined}
    dateRange={dateRange}
    variant="range"
    monthCount={2}
/>`;

const CAPTION_CODE = `<DateSelector
    selectMultipleObject={() => undefined}
    maxDate={new Date(2030, 11, 31)}
    minDate={new Date(2020, 0, 1)}
    captionLayout="dropdown"
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const WEEKDAY_LENGTHS = [
    "very-short",
    "short",
    "long"
] as const;

const WEEKDAY_LENGTH_KEYS = {
    "very-short": "dateSelectorPage.weekdayVeryShort",
    short: "dateSelectorPage.weekdayShort",
    long: "dateSelectorPage.weekdayLong"
} as const;

const CONTROLS_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const TODAY = new Date();

const addDays = (date: Date, count: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);

const addMonths = (date: Date, count: number) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const LIMIT_DAYS = 10;

const LIMIT_MAX_DATE = addDays(TODAY, LIMIT_DAYS);

const CAPTION_MIN_DATE = new Date(2020, 0, 1);

const CAPTION_MAX_DATE = new Date(2030, 11, 31);

const RANGE_MIN_DAYS = 3;

const RANGE_MAX_DAYS = 7;

const MONTHS_IN_YEAR = 12;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("dateSelectorPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("dateSelectorPage.limitsTitle"),
        id: "limits"
    },
    {
        title: t("dateSelectorPage.displayTitle"),
        id: "display"
    },
    {
        title: t("dateSelectorPage.viewTitle"),
        id: "view"
    },
    {
        title: t("dateSelectorPage.weeksTitle"),
        id: "weeks"
    },
    {
        title: t("dateSelectorPage.monthsTitle"),
        id: "months"
    },
    {
        title: t("dateSelectorPage.captionTitle"),
        id: "caption"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DateSelectorPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const calendarRef = useRef<IDateSelectorRef>(null);

    const [
        date,
        setDate
    ] = useState<Date | undefined>(undefined);

    const [
        dateRange,
        setDateRange
    ] = useState<DateTimePickerDateRange | undefined>(undefined);

    const [
        limitDate,
        setLimitDate
    ] = useState<Date | undefined>(undefined);

    const [
        displayDate,
        setDisplayDate
    ] = useState<Date | undefined>(undefined);

    const [
        viewedDate,
        setViewedDate
    ] = useState<Date | undefined>(undefined);

    const [
        weeksDate,
        setWeeksDate
    ] = useState<Date | undefined>(undefined);

    const [
        monthsRange,
        setMonthsRange
    ] = useState<DateTimePickerDateRange | undefined>(undefined);

    const [
        captionDate,
        setCaptionDate
    ] = useState<Date | undefined>(undefined);

    const [
        weekdayLength,
        setWeekdayLength
    ] = useState<(typeof WEEKDAY_LENGTHS)[number]>("short");

    const [
        isLocaleWeek,
        setIsLocaleWeek
    ] = useState(true);

    const [
        isTodayShown,
        setIsTodayShown
    ] = useState(true);

    const formatDate = (value: Date) => formatDateTime({
        locale: activeLocale,
        preset: "date",
        date: value
    });

    const getDateCaption = () => {
        if(!date) {
            return t("dateSelectorPage.overviewCaption");
        }

        return t("dateSelectorPage.selectedCaption", [
            formatDate(date)
        ]);
    };

    const getRangeCaption = () => {
        if(!dateRange?.start) {
            return t("dateSelectorPage.rangeEmptyCaption", [
                RANGE_MIN_DAYS,
                RANGE_MAX_DAYS
            ]);
        }

        if(!dateRange.end) {
            return t("dateSelectorPage.rangeStartCaption", [
                formatDate(dateRange.start)
            ]);
        }

        return t("dateSelectorPage.rangeCaption", [
            formatDate(dateRange.start),
            formatDate(dateRange.end)
        ]);
    };

    const getLimitCaption = () => {
        if(!limitDate) {
            return t("dateSelectorPage.limitsCaption", [
                LIMIT_DAYS
            ]);
        }

        return t("dateSelectorPage.selectedCaption", [
            formatDate(limitDate)
        ]);
    };

    const handleToday = () => {
        calendarRef.current?.changeCurrentMonth(new Date());
    };

    const handleNextYear = () => {
        calendarRef.current?.changeCurrentMonth(addMonths(TODAY, MONTHS_IN_YEAR));
    };

    return <Article>
        <ComponentHeader
            lead={t("dateSelectorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("dateSelectorPage.overviewText")}
            </Paragraph>
            <Preview
                caption={getDateCaption()}
            >
                <DateSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setDate}
                    variant="single"
                    date={date}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("dateSelectorPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("dateSelectorPage.rangeText")}
            </Paragraph>
            <Preview
                caption={getRangeCaption()}
            >
                <DateSelector
                    multipleSelectMinimumRequiredDayCount={RANGE_MIN_DAYS}
                    multipleSelectDayLimit={RANGE_MAX_DAYS}
                    selectMultipleObject={setDateRange}
                    selectObject={() => undefined}
                    dateRange={dateRange}
                    variant="range"
                />
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateSelectorPage.limitsTitle")}
            id="limits"
        >
            <Paragraph>
                {t("dateSelectorPage.limitsText")}
            </Paragraph>
            <Preview
                caption={getLimitCaption()}
            >
                <DateSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setLimitDate}
                    maxDate={LIMIT_MAX_DATE}
                    date={limitDate}
                    variant="single"
                    minDate={TODAY}
                />
            </Preview>
            <CodeBlock
                code={LIMITS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateSelectorPage.displayTitle")}
            id="display"
        >
            <Paragraph>
                {t("dateSelectorPage.displayText")}
            </Paragraph>
            <Preview>
                <DateSelector
                    localeBasedFirstDayOfWeek={isLocaleWeek}
                    selectMultipleObject={() => undefined}
                    isShowTodayIndicator={isTodayShown}
                    dayOfWeekLength={weekdayLength}
                    selectObject={setDisplayDate}
                    date={displayDate}
                    variant="single"
                />
                <div
                    style={CONTROLS_STYLE}
                >
                    <Switch
                        title={t("dateSelectorPage.localeWeek")}
                        onCheckedChange={setIsLocaleWeek}
                        isChecked={isLocaleWeek}
                    />
                    <Switch
                        title={t("dateSelectorPage.todayMarker")}
                        onCheckedChange={setIsTodayShown}
                        isChecked={isTodayShown}
                    />
                    <div
                        aria-label={t("dateSelectorPage.weekdayLabel")}
                        role="radiogroup"
                    >
                        {
                            WEEKDAY_LENGTHS.map((length) => <RadioButton
                                onPress={() => setWeekdayLength(length)}
                                title={t(WEEKDAY_LENGTH_KEYS[length])}
                                isChecked={weekdayLength === length}
                                key={length}
                            />)
                        }
                    </div>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("dateSelectorPage.viewTitle")}
            id="view"
        >
            <Paragraph>
                {t("dateSelectorPage.viewText")}
            </Paragraph>
            <Preview>
                <DateSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setViewedDate}
                    date={viewedDate}
                    ref={calendarRef}
                    variant="single"
                />
                <div
                    style={CONTROLS_STYLE}
                >
                    <Button
                        title={t("dateSelectorPage.goToday")}
                        onPress={handleToday}
                        variant="outline"
                        type="neutral"
                    />
                    <Button
                        title={t("dateSelectorPage.goNextYear")}
                        onPress={handleNextYear}
                        variant="outline"
                        type="neutral"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={REF_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateSelectorPage.weeksTitle")}
            id="weeks"
        >
            <Paragraph>
                {t("dateSelectorPage.weeksText")}
            </Paragraph>
            <Preview
                caption={t("dateSelectorPage.weeksCaption")}
            >
                <DateSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setWeeksDate}
                    isShowWeekNumber={true}
                    variant="single"
                    date={weeksDate}
                />
            </Preview>
            <CodeBlock
                code={WEEKS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateSelectorPage.monthsTitle")}
            id="months"
        >
            <Paragraph>
                {t("dateSelectorPage.monthsText")}
            </Paragraph>
            <Preview
                caption={t("dateSelectorPage.monthsCaption")}
            >
                <DateSelector
                    selectMultipleObject={setMonthsRange}
                    selectObject={() => undefined}
                    dateRange={monthsRange}
                    variant="range"
                    monthCount={2}
                />
            </Preview>
            <CodeBlock
                code={MONTHS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateSelectorPage.captionTitle")}
            id="caption"
        >
            <Paragraph>
                {t("dateSelectorPage.captionText")}
            </Paragraph>
            <Preview
                caption={t("dateSelectorPage.captionCaption")}
            >
                <DateSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setCaptionDate}
                    maxDate={CAPTION_MAX_DATE}
                    minDate={CAPTION_MIN_DATE}
                    captionLayout="dropdown"
                    date={captionDate}
                    variant="single"
                />
            </Preview>
            <CodeBlock
                code={CAPTION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("dateSelectorPage.a11yStructure"),
                    t("dateSelectorPage.a11yDay"),
                    t("dateSelectorPage.a11yMonth"),
                    t("dateSelectorPage.a11yKeyboard"),
                    t("dateSelectorPage.a11yLimit"),
                    t("dateSelectorPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("dateSelectorPage.propVariant"),
                        type: "\"single\" | \"range\" | \"rrule\"",
                        name: "variant"
                    },
                    {
                        description: t("dateSelectorPage.propSelectObject"),
                        type: "(date?: Date) => void",
                        name: "selectObject"
                    },
                    {
                        description: t("dateSelectorPage.propSelectMultipleObject"),
                        type: "(dateRange?: DateTimePickerDateRange) => void",
                        name: "selectMultipleObject"
                    },
                    {
                        description: t("dateSelectorPage.propDate"),
                        name: "date",
                        type: "Date"
                    },
                    {
                        description: t("dateSelectorPage.propDateRange"),
                        type: "DateTimePickerDateRange",
                        name: "dateRange"
                    },
                    {
                        description: t("dateSelectorPage.propViewDate"),
                        name: "viewDate",
                        type: "Date"
                    },
                    {
                        description: t("dateSelectorPage.propMinDate"),
                        name: "minDate",
                        type: "Date"
                    },
                    {
                        description: t("dateSelectorPage.propMaxDate"),
                        name: "maxDate",
                        type: "Date"
                    },
                    {
                        description: t("dateSelectorPage.propMultipleSelectDayLimit"),
                        name: "multipleSelectDayLimit",
                        type: "number"
                    },
                    {
                        description: t("dateSelectorPage.propMultipleSelectMinimumRequiredDayCount"),
                        name: "multipleSelectMinimumRequiredDayCount",
                        type: "number"
                    },
                    {
                        description: t("dateSelectorPage.propIsShowTodayIndicator"),
                        name: "isShowTodayIndicator",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateSelectorPage.propDayOfWeekLength"),
                        type: "\"short\" | \"very-short\" | \"long\"",
                        defaultValue: "\"short\"",
                        name: "dayOfWeekLength"
                    },
                    {
                        description: t("dateSelectorPage.propLocaleBasedFirstDayOfWeek"),
                        name: "localeBasedFirstDayOfWeek",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateSelectorPage.propIsShowWeekNumber"),
                        name: "isShowWeekNumber",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateSelectorPage.propMonthCount"),
                        name: "monthCount",
                        defaultValue: "1",
                        type: "1 | 2"
                    },
                    {
                        description: t("dateSelectorPage.propIsShowCard"),
                        defaultValue: "true",
                        name: "isShowCard",
                        type: "boolean"
                    },
                    {
                        description: t("dateSelectorPage.propCaptionLayout"),
                        type: "\"label\" | \"dropdown\"",
                        defaultValue: "\"label\"",
                        name: "captionLayout"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("dateSelectorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("dateSelectorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("dateSelectorPage.propsRefIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateSelectorPage.refChangeCurrentMonth"),
                        type: "(date: Date) => void",
                        name: "changeCurrentMonth"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dateSelectorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DateSelectorPage;
